import { create } from 'zustand'

import { tokenStorage } from '@/lib/token-storage'
import { ApiError, ApiErrorCode, toApiError } from '@/lib/api/api-error'
import { decodeAccessToken, isAccessTokenExpired } from '@/lib/jwt'
import { refreshAccessToken } from '@/lib/api/http'
import { authApi, toDisplayName, toUserProfile } from '@/features/auth/api'
import type { AuthIdentity, AuthUser, LoginCredentials, UserProfile } from '@/features/auth/types'

/**
 * The identity the whole session hangs on. Three sources, in order of fidelity:
 * the user object the login response supplied, the snapshot persisted from it,
 * and finally the JWT's own claims.
 *
 * The token is the source that always exists — the deployed auth service is not
 * the repo's build and need not return a user at all — so nothing may *require*
 * the other two. `mustChangePassword` is the exception the token cannot carry;
 * it survives only via the snapshot.
 */
function identityFromClaims(): AuthIdentity | null {
  const claims = decodeAccessToken(tokenStorage.getAccessToken() ?? '')
  const id = claims?.sub ?? claims?.userId ?? claims?.id
  if (!claims || !id) return null

  return {
    id,
    username: claims.username ?? claims.email ?? claims.name ?? '',
    roles: claims.roles ?? [],
    mustChangePassword: false,
  }
}

function identityOf(user: AuthUser | UserProfile | null): AuthIdentity | null {
  const known = user ?? tokenStorage.getUser<AuthUser>()
  if (known?.id) {
    return {
      id: known.id,
      username: known.username ?? '',
      roles: known.roles ?? [],
      mustChangePassword: known.mustChangePassword ?? false,
    }
  }

  return identityFromClaims()
}

/** Widens an identity into a renderable user when no richer profile is available. */
function userOf(identity: AuthIdentity, from?: Partial<AuthUser> | null): AuthUser {
  const known = from ?? tokenStorage.getUser<AuthUser>()

  return {
    id: identity.id,
    // Both sources hold the login identifier, which for this service is an email
    // address — so the name is derived, not taken verbatim. This is the value
    // the header and the greeting render before the profile arrives.
    name: toDisplayName(known?.name || identity.username) || 'Account',
    username: identity.username || known?.username || '',
    email: known?.email ?? '',
    roles: identity.roles,
    mustChangePassword: identity.mustChangePassword,
  }
}

export type AuthStatus =
  /** Nothing has been attempted yet. */
  | 'idle'
  /** Trading the stored refresh token for a session. */
  | 'bootstrapping'
  | 'authenticated'
  | 'unauthenticated'

interface AuthState {
  user: AuthUser | UserProfile | null
  status: AuthStatus
  isBootstrapped: boolean

  bootstrap: () => Promise<void>
  login: (credentials: LoginCredentials) => Promise<AuthUser>
  logout: () => Promise<void>
  reloadProfile: () => Promise<void>
  setUser: (user: AuthUser | UserProfile) => void
  clearSession: () => void
}

type SetState = (partial: Partial<AuthState>) => void
type GetState = () => AuthState

/** In-flight boot and profile fetch, each shared by every caller until it settles. */
let bootstrapInFlight: Promise<void> | null = null
let profileInFlight: Promise<void> | null = null

async function runBootstrap(set: SetState, get: GetState): Promise<void> {
  if (!tokenStorage.getRefreshToken()) {
    set({ status: 'unauthenticated', user: null, isBootstrapped: true })
    return
  }

  set({ status: 'bootstrapping' })

  try {
    // A reload does not need a new token — only an unexpired one. Since the
    // service rotates on every refresh, skipping this when the stored token is
    // still good is what keeps a normal reload from spending a rotation (and
    // from racing a second tab for it).
    if (isAccessTokenExpired(tokenStorage.getAccessToken())) {
      // One shared single-flight path: a second route to the endpoint would
      // spend a token the first had already replaced.
      await refreshAccessToken()
    }

    const identity = identityOf(null)
    if (!identity) throw new Error('access token carries no identity')

    // The session stands on the refreshed token alone — commit it before
    // reaching for anything optional.
    set({ user: userOf(identity), status: 'authenticated', isBootstrapped: true })
  } catch {
    tokenStorage.clear()
    set({ user: null, status: 'unauthenticated', isBootstrapped: true })
    return
  }

  await get().reloadProfile()
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  status: 'idle',
  isBootstrapped: false,

  /**
   * Runs once on app start, no matter how many times it is called.
   *
   * Only the refresh call decides whether the session lives. Enriching the user
   * from the main API's profile is best effort — it is a second service with its
   * own availability and its own idea of a valid caller, and it answers a bad
   * request with HTTP 400 "No user signed in." rather than a 401. Letting that
   * reach the session teardown is what used to sign the user out on every
   * reload, so it stays sealed off in `reloadProfile`.
   */
  bootstrap() {
    // Idempotent, and deliberately not guarded on `status`. React StrictMode
    // mounts the effect twice, and when the stored access token is still valid
    // there is no `await` before the status reaches 'authenticated' — so a
    // status check is already stale by the time the second call arrives, and
    // both would go on to fetch the profile. The latch is the only thing that
    // holds across both.
    if (get().isBootstrapped) return Promise.resolve()

    bootstrapInFlight ??= runBootstrap(set, get).finally(() => {
      bootstrapInFlight = null
    })

    return bootstrapInFlight
  },
  async login(credentials) {
    try {
      const { accessToken, refreshToken, user } = await authApi.login(credentials)
      const persistent = credentials.rememberMe ?? true

      // Only the tokens are load-bearing. A `user` object is welcome but not
      // required — the JWT carries the same identity, and the deployed service
      // is not obliged to send one.
      if (!accessToken || !refreshToken) {
        throw new ApiError(
          'Sign-in did not return a session token. Please contact your NexQR administrator.',
          { status: 502, code: ApiErrorCode.Unknown },
        )
      }

      // Refresh token first: it is what defines which store the session lives in,
      // and the other two writes default to "wherever the session already is".
      tokenStorage.setRefreshToken(refreshToken, persistent)
      tokenStorage.setAccessToken(accessToken, persistent)

      const identity = user?.id
        ? {
            id: user.id,
            username: user.username ?? '',
            roles: user.roles ?? [],
            mustChangePassword: user.mustChangePassword ?? false,
          }
        : identityFromClaims()

      // Marking the store `authenticated` without a user would send the guards
      // into a /login -> /dashboard -> /login bounce that reads as "login did
      // nothing"; refuse the session instead so the failure is visible.
      if (!identity) {
        throw new ApiError(
          'Sign-in returned a token this app could not read. Please contact your NexQR administrator.',
          { status: 502, code: ApiErrorCode.Unknown },
        )
      }

      const sessionUser = userOf(identity, user)
      tokenStorage.setUser(sessionUser, persistent)
      set({ user: sessionUser, status: 'authenticated', isBootstrapped: true })

      return sessionUser
    } catch (error) {
      tokenStorage.clear()
      set({ user: null, status: 'unauthenticated', isBootstrapped: true })
      throw toApiError(error)
    }
  },

  async logout() {
    const refreshToken = tokenStorage.getRefreshToken()

    try {
      if (refreshToken) await authApi.logout(refreshToken)
    } catch {
      // Revoking server-side is best effort — the local session goes either way.
    } finally {
      get().clearSession()
    }
  },

  /**
   * Folds the main API's profile into the signed-in user. Never throws and
   * never ends the session: the profile is decoration on an identity the auth
   * service has already vouched for.
   */
  reloadProfile() {
    const identity = identityOf(get().user)
    if (!identity) return Promise.resolve()

    // Single-flight: concurrent callers share one request rather than each
    // firing their own.
    profileInFlight ??= authApi
      .profile()
      .then((dto) => {
        set({ user: toUserProfile(dto, identity), status: 'authenticated' })
      })
      .catch(() => {
        // Leave the token-derived user in place — the profile is decoration on
        // an identity the auth service has already vouched for.
      })
      .finally(() => {
        profileInFlight = null
      })

    return profileInFlight
  },

  setUser(user) {
    set({ user })
  },

  clearSession() {
    bootstrapInFlight = null
    profileInFlight = null
    tokenStorage.clear()
    set({ user: null, status: 'unauthenticated', isBootstrapped: true })
  },
}))

/** A rejected refresh token anywhere in the app tears the session down here. */
tokenStorage.onSessionExpired(() => {
  useAuthStore.getState().clearSession()
})
